import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  MAX_STEP_ID,
  type ScreeningStepsDocument,
} from "@/lib/screeningDocuments";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Field, Textarea } from "@/ui/Input";
import { Tooltip } from "@/ui/Tooltip";

/** The service's own bound (`ScreeningStepsDocument.steps`, max 64). */
const MAX_STEPS = 64;

/**
 * `<slug>_screening_steps.yaml` as an ordered instruction form (#912).
 *
 * ## Two orderings, and only one of them is here
 *
 * `PathwayStepList` orders the asset's ROLES — Base prompt, Classification,
 * Diagnosis, Schema, Steps — which is `parts` order. THIS form orders the
 * instructions INSIDE the `screening_steps` document, which is `steps` order in
 * one part's data. Conflating them is the mistake this file exists to avoid: a
 * clinician moving "ask about the event" above "ask about re-living it" is not
 * moving the Steps document above the Schema document.
 *
 * ## Why the id is shown and never edited
 *
 * LIST ORDER IS CLINICAL ORDER — `prompts.format_steps` walks `steps` in
 * document order and NUMBERS THE PROMPT FROM THAT POSITION — while `id` is
 * stable identity: clinical memory skips and enriches by it (`step_id in
 * skip_step_ids`), and a diagnosis mapping's `stepN` names it. The two are
 * deliberately independent, so a move changes the order and touches no id, and
 * the row shows its id as the fact a diagnosis rule will refer to rather than
 * as a position. Renumbering on reorder would silently redirect every rule and
 * memory entry that named a step.
 *
 * A new step takes the lowest unused positive id rather than `length + 1`:
 * after removing step 2 of three, `length + 1` is 3, which the remaining step 3
 * already holds — and duplicate ids are what `_validate_step_ids` refuses,
 * because two steps sharing one id cannot be addressed separately.
 *
 * ## The reorder controls are `PathwayStepList`'s, deliberately
 *
 * Move up / Move down with focus moved to the row's opposite control when the
 * pressed one goes inert, plus a polite live region naming the step and its new
 * position. That pair is already reasoned about at length one file over
 * (#643/#455), a clinician meets both lists in the same session, and a second
 * reorder idiom on the screen behind the first would be a different answer to
 * the same question. The two are not merged for the reason that file gives:
 * rewriting the list the main-concern surface depends on, from a ticket scoped
 * to screening documents, is how a scoped change becomes an unreviewable one.
 */
export function ScreeningStepsForm({
  document,
  reservedStepIds,
  readOnly,
  onChange,
}: {
  document: ScreeningStepsDocument;
  /**
   * Every step id this asset has retired, or `null` when that is not known yet.
   *
   * A RULE IS ADDRESSED TO A STEP BY ID, so an id that is free in the open
   * document is not necessarily free in the asset: `mandatory_roles.step3`
   * outlives the step 3 it was written for, and a step removed and PUBLISHED
   * leaves the head with no trace of the id at all.
   *
   * `null` means the version history has not been read, and Add is REFUSED
   * rather than allocating from a mark this form could not confirm — the same
   * fail-closed direction the manifest read takes one screen up. See
   * `nextStepId`.
   */
  // REQUIRED, and `null` is the only spelling of "not known". It was
  // optional while both guards tested `=== null`, so an omitted prop slipped
  // past the refusal and allocated from an empty reserved set — silently
  // RECYCLING an id a retired step's `mandatory_roles.stepN` may still name,
  // which is the one outcome this mark exists to prevent.
  reservedStepIds: ReadonlySet<number> | null;
  readOnly: boolean;
  /** The whole document, replaced. The caller re-serialises it into the draft. */
  onChange: (next: ScreeningStepsDocument) => void;
}) {
  // `useMemo` because the effect below depends on this array: `?? []` mints a
  // NEW empty array on every render for a document without `steps`, which
  // would re-run the focus effect on renders that changed nothing.
  const steps = useMemo(() => document.steps ?? [], [document.steps]);
  // Keyed on the step's ID rather than its index, for `PathwayStepList`'s
  // reason: the index is exactly what a move changes, so a queued index would
  // restore focus onto whichever row moved into the old slot.
  const restore = useRef<{ id: number; direction: "up" | "down" } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const controls = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    const pending = restore.current;
    if (pending === null) {
      return;
    }
    restore.current = null;
    const index = steps.findIndex((step) => step.id === pending.id);
    if (index === -1) {
      return;
    }
    const stillOperable =
      pending.direction === "up" ? index > 0 : index < steps.length - 1;
    if (!stillOperable) {
      const opposite = pending.direction === "up" ? "down" : "up";
      controls.current.get(`${String(pending.id)}:${opposite}`)?.focus();
    }
  }, [steps]);

  function replace(next: ScreeningStepsDocument["steps"]) {
    // SPREAD THE DOCUMENT rather than building `{ steps }`: a stored document
    // may carry keys this form does not render — it is one immutable version of
    // a pack file, and the schema can gain a field before this form does — and
    // rebuilding from scratch would silently drop them on the first edit.
    onChange({ ...document, steps: next });
  }

  function move(index: number, direction: "up" | "down") {
    const to = direction === "up" ? index - 1 : index + 1;
    const next = [...steps];
    const [moved] = next.splice(index, 1);
    if (moved === undefined) {
      return;
    }
    next.splice(to, 0, moved);
    restore.current = { id: moved.id, direction };
    setAnnouncement(
      `Step ${String(moved.id)} moved to position ${String(to + 1)} of ${String(steps.length)}.`,
    );
    replace(next);
  }

  const removalRefusal =
    steps.length <= 1
      ? "A screening must keep at least one step. Edit this one instead of removing it."
      : null;
  const nextId =
    reservedStepIds === null ? null : nextStepId(steps, reservedStepIds);
  const addRefusal =
    steps.length >= MAX_STEPS
      ? `A screening carries at most ${String(MAX_STEPS)} steps.`
      : reservedStepIds === null
        ? "This screening's published step ids could not be read, so a new step cannot be given one that is certainly free."
        : // IDS ARE RETIRED RATHER THAN RECYCLED, so the id space is exhausted
          // by the highest id ever used and not by the number of rows: a
          // document holding step 9999 has no room for another step even with
          // three rows on screen. Refused here rather than at a save the
          // service is certain to reject.
          nextId !== null && nextId > MAX_STEP_ID
          ? `This screening has used every step id up to ${String(MAX_STEP_ID)}, so no further step can be added.`
          : null;

  return (
    <div className="space-y-3">
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <ol
        aria-label="Screening instructions, in the order they are asked"
        className="space-y-3"
      >
        {steps.map((step, index) => (
          <li
            key={step.id}
            className="rounded-md border border-border bg-card p-3"
          >
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-xs tabular-nums text-muted-foreground">
                {index + 1}.
              </span>
              {/* THE ID, AS A FACT RATHER THAN A CONTROL. A diagnosis rule names
                  this number, so it is legible here; it is not editable because
                  changing it would re-point every rule and memory entry that
                  already refers to it. */}
              <span className="text-sm font-medium text-foreground">
                Step {step.id}
              </span>

              {readOnly ? null : (
                <div className="ms-auto flex items-center gap-0.5">
                  {/* The step's own id is in every label, for PW-LIB-09's
                      reason: four identical "Move up" controls give a
                      screen-reader user four indistinguishable items. The
                      POSITION is deliberately not in the name — it changes on
                      every move, and a name that changes under a focused
                      control is re-announced as a different button. */}
                  <IconButton
                    ref={(element) => {
                      const key = `${String(step.id)}:up`;
                      if (element) controls.current.set(key, element);
                      else controls.current.delete(key);
                    }}
                    label={`Move step ${String(step.id)} up`}
                    icon={<ArrowUp className="h-4 w-4" />}
                    disabled={index === 0}
                    onClick={() => move(index, "up")}
                  />
                  <IconButton
                    ref={(element) => {
                      const key = `${String(step.id)}:down`;
                      if (element) controls.current.set(key, element);
                      else controls.current.delete(key);
                    }}
                    label={`Move step ${String(step.id)} down`}
                    icon={<ArrowDown className="h-4 w-4" />}
                    disabled={index === steps.length - 1}
                    onClick={() => move(index, "down")}
                  />
                  {/* ONE TOOLTIP. `IconButton` wraps itself in a `Tooltip`
                      carrying its label unless told not to, so nesting this one
                      around it anchors two Radix tooltips to a single trigger.
                      Fixed in `PathwayStepList` first and missed here, in a file
                      the same commit was already editing. */}
                  <Tooltip
                    content={removalRefusal ?? `Remove step ${String(step.id)}`}
                  >
                    <IconButton
                      label={`Remove step ${String(step.id)}`}
                      icon={<Trash2 className="h-4 w-4" />}
                      showTooltip={false}
                      disabled={removalRefusal !== null}
                      onClick={() =>
                        replace(steps.filter((_, at) => at !== index))
                      }
                    />
                  </Tooltip>
                </div>
              )}
            </div>

            <Field label={`Instruction for step ${String(step.id)}`}>
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={4}
                  readOnly={readOnly}
                  value={step.instruction}
                  onChange={(event) =>
                    replace(
                      steps.map((candidate, at) =>
                        at === index
                          ? { ...candidate, instruction: event.target.value }
                          : candidate,
                      ),
                    )
                  }
                />
              )}
            </Field>
          </li>
        ))}
      </ol>

      {readOnly ? null : (
        <Tooltip content={addRefusal ?? "Add a step to the end of this screening"}>
          <Button
            variant="outline"
            size="sm"
            disabled={addRefusal !== null}
            onClick={() => {
              if (nextId === null || nextId > MAX_STEP_ID) {
                return;
              }
              replace([...steps, { id: nextId, instruction: "" }]);
            }}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            Add step
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * An id no step holds and no rule or published version has ever claimed.
 *
 * ONE ABOVE THE HIGHEST CLAIM, never the lowest gap, and the difference is a
 * clinical-safety one rather than a stylistic one. A diagnosis mapping addresses
 * a step by id (`mandatory_roles.step3`), clinical memory skips and enriches by
 * it, and neither is edited on this screen — the mapping is a sibling document,
 * and clinical memory is not in this asset at all. Handing the next added step a
 * retired id would attach those to a brand-new instruction, silently.
 *
 * FILLING A GAP IS THE SAME MISTAKE AS REUSING THE LAST ID, which is why the
 * current document cannot be the only input: `[1, 2, 3]` with step 3 removed
 * leaves a maximum of 2, and 3 is exactly the id that must not come back. So
 * `reserved` carries the asset's whole published history as well as the draft's
 * live references, and this only ever counts upward from it.
 */
export function nextStepId(
  steps: readonly { id: number }[],
  reserved: ReadonlySet<number> = new Set(),
): number {
  const claimed = [...steps.map((step) => step.id), ...reserved];
  return claimed.length === 0 ? 1 : Math.max(...claimed, 0) + 1;
}
